import _ from 'lodash';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Drawer, Box, Typography, Button, SxProps, useTheme, Theme } from '@mui/material';
import { IFilter } from './interfaces';
import { UserConfigurableModule } from '../../../types/User';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useGetCurrentUserFilters } from '../../../services/react-query/users/useGetUserFilters';
import { useSaveUserFilter } from '../../../services/react-query/users/useSaveUserFilter';
import RenderSelector from './RenderSelector';
import SaveFilterModal from './SaveFilterModal';
import SavedFilterPicker from './SavedFilterPicker';
import { useDeleteUserFilter } from '../../../services/react-query/users/useDeleteUserFilter';
import { Loading } from '../../../uikit';
import { useGetCurrentUserSettings } from '../../../services/react-query/users/useGetCurrentUserSettings';
import { useUpdateUserSettings } from '../../../services/react-query/users/useUpdateUserSettings';
import SearchBar from './SearchBar';
import { FilterContent } from '../../../types/Filter';
import { defaultContainerStyling } from '../../../styles/sxStyling';

interface FilterSidebarProps {
  moduleFilters: IFilter[];
  module: UserConfigurableModule;
  open: boolean;
  onClose: () => void;
  showSearch?: boolean;
  activeFilter?: FilterContent;
  setActiveFilter: (filterState: FilterContent) => void;
}

const panelHeadersx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 0.6,
  bgcolor: 'var(--table-header-bg)',
};
const panelFooterSx: SxProps = {
  p: 2,
  display: 'flex',
  gap: 1,
  bgcolor: 'background.paper',
  zIndex: 1,
  justifyContent: 'space-between',
};
const panelSx: SxProps<Theme> = {
  ...defaultContainerStyling,
  width: 300,
  height: '100%',
  bgcolor: 'background.paper',
  overflow: 'hidden',
  border: '1px solid var(--border-color)',
};

export default function FilterSidebar({ moduleFilters, module, open, onClose, showSearch = true, activeFilter, setActiveFilter }: FilterSidebarProps) {
  const theme = useTheme();
  const { isMobile } = useIsMobile();

  const filtersByKey = useMemo(() => _.keyBy(moduleFilters, 'key'), [moduleFilters]);
  const filterKeys = useMemo(() => Object.keys(filtersByKey), [filtersByKey]);

  const { mutate: saveUserFilter } = useSaveUserFilter();
  const { mutate: deleteUserFilter } = useDeleteUserFilter();
  const { mutate: updateUserSettings } = useUpdateUserSettings();

  const { data: settingsResponse, isLoading: settingsLoading } = useGetCurrentUserSettings();
  const { data: savedFilters, isLoading: filtersLoading } = useGetCurrentUserFilters({ page: module });

  const filtersByName = useMemo(() => _.keyBy(_.sortBy(savedFilters, 'name'), 'name'), [savedFilters]);
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);

  const lastUserFiltersRef = useRef<FilterContent>();

  // This mimics a plain state-change API but actually keeps values synced w/ the backend
  const selectedFilter = useMemo(() => {
    return settingsResponse?.settings?.filter_selection?.[module] ?? '';
  }, [settingsResponse, module]);

  const setSelectedFilter = useCallback(
    (name: string) => {
      if (!settingsResponse) return;
      if (settingsResponse.settings?.filter_selection?.[module] === name) return;

      updateUserSettings({
        filter_selection: { [module]: name },
      });
    },
    [settingsResponse]
  );

  // Load persisted filters from backend EVEN IF THE DRAWER IS CLOSED
  useEffect(() => {
    if (savedFilters === undefined) return;
    const filter = filtersByName[selectedFilter]?.content ?? {};
    if (_.isEqual(lastUserFiltersRef.current, filter)) return;
    lastUserFiltersRef.current = filter;

    if (_.isEqual(activeFilter, filter)) return;

    setActiveFilter(filter);
  }, [filtersByName, filterKeys, selectedFilter, savedFilters, activeFilter]);

  // Apply change and reset the selection to the default if a named filter was being used
  const makeOnChange = useCallback(
    (key: string, vals: (string | null)[]) => {
      const newFilter = _.pickBy({ ...activeFilter, [key]: vals }, (v) => !(_.isEmpty(v) || _.every(v, _.isNull)));

      const onSuccess = () => {
        setActiveFilter(newFilter);
        if (selectedFilter !== '') setSelectedFilter('');
      };

      saveUserFilter(
        {
          name: '',
          page: module,
          content: newFilter,
        },
        { onSuccess }
      );
    },
    [activeFilter, selectedFilter]
  );

  if (activeFilter === undefined || settingsLoading || filtersLoading) return <Loading />;

  const handleClear = () => {
    setSelectedFilter('');
    saveUserFilter({
      name: '',
      page: module,
      content: {},
    });
    setActiveFilter({});
  };

  const handleSave = (name: string) => {
    if (activeFilter === null) return;
    saveUserFilter({
      name,
      page: module,
      content: activeFilter,
    });
    setSelectedFilter(name);
    setSaveModalOpen(false);
  };

  const handleDelete = (name: string) => {
    const filter = filtersByName[name];
    if (!filter) return;

    deleteUserFilter(filter);
    if (name === selectedFilter) setSelectedFilter('');
  };

  const menuBarHeight = theme.mixins.toolbar.minHeight || 64;

  const panel = (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: isMobile ? '100vh' : `calc(100vh - ${menuBarHeight}px - 100px)`,
        }}
      >
        <Box sx={panelHeadersx}>
          <Typography variant="h6">Filters</Typography>
        </Box>

        {showSearch && <SearchBar />}

        {/* Sections */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <SavedFilterPicker names={_.keys(filtersByName)} value={selectedFilter} onSelect={setSelectedFilter} onDelete={handleDelete} />
          {filterKeys.map((key) => (
            <RenderSelector key={key} filter={filtersByKey[key]!} values={activeFilter[key] ?? []} onChange={makeOnChange} />
          ))}
        </Box>

        {/* Footer with actions */}
        <Box
          sx={{
            ...panelFooterSx,
            ...(isMobile ? { position: 'relative', bottom: 0, right: 0, width: '100%' } : { position: 'sticky', bottom: 0 }),
          }}
        >
          <Button size="small" variant="outlined" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="contained" onClick={() => setSaveModalOpen(true)}>
            Save
          </Button>
        </Box>
      </Box>
      <SaveFilterModal open={saveModalOpen} names={_.keys(filtersByName)} onClose={() => setSaveModalOpen(false)} onSave={handleSave} />
    </>
  );

  // Desktop inline vs mobile drawer
  if (!isMobile) {
    if (!open) return null;
    return <Box sx={panelSx}>{panel}</Box>;
  }

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box sx={{ width: 300, height: '100vh' }}>{panel}</Box>
    </Drawer>
  );
}
